import { useQuery } from '@tanstack/react-query'
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { veiculosApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Card, CardBody, CardHeader } from '../../components/ui/Card'
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/EmptyState'
import { TendenciaConsumo, TIPO_COMBUSTIVEL_LABEL, TIPO_TRECHO_LABEL, UnidadeEnergia, UNIDADE_ENERGIA_LABEL } from '../../lib/enums'
import { formatDate, formatMoney } from '../../lib/utils'
import type { ConsumoEnergia, TrechoConsumo } from '../../types'
import { formatKm, numeroBr, unidadeConsumo } from './veiculoUtils'

const AXIS_TICK = { fontSize: 11, fill: '#8B949E' }

export function TendenciaBadge({ t, variacao }: { t: TendenciaConsumo; variacao?: number | null }) {
  if (t === TendenciaConsumo.Insuficiente) return <Badge variant="muted">Poucos dados</Badge>
  const [icone, variante, rotulo] = t === TendenciaConsumo.Melhorando ? ['ri-arrow-right-up-line', 'income', 'Melhorando'] as const
    : t === TendenciaConsumo.Piorando ? ['ri-arrow-right-down-line', 'expense', 'Piorando'] as const
    : ['ri-subtract-line', 'muted', 'Estável'] as const
  return <Badge variant={variante} className="normal-case"><i className={icone} aria-hidden /> {rotulo}{variacao != null ? ` (${numeroBr(Math.abs(variacao), 1)}%)` : ''}</Badge>
}

function Kpi({ rotulo, valor, sub }: { rotulo: string; valor: string; sub?: string }) {
  return (
    <div className="rounded bg-surface2 p-3">
      <p className="text-2xs uppercase text-muted">{rotulo}</p>
      <p className="mt-1 text-xl font-semibold text-white">{valor}</p>
      {sub && <p className="text-2xs text-muted">{sub}</p>}
    </div>
  )
}

function TrechoLinha({ rotulo, t, un }: { rotulo: string; t?: TrechoConsumo | null; un: string }) {
  if (!t) return null
  return (
    <div className="rounded bg-surface2 p-3 text-13">
      <p className="text-2xs uppercase text-muted">{rotulo}</p>
      <p className="mt-1 text-lg font-semibold text-white">{t.kmPorUnidade != null ? `${numeroBr(t.kmPorUnidade, 1)} ${un}` : '—'}</p>
      <p className="text-2xs text-muted">{formatDate(t.dataInicio)} a {formatDate(t.dataFim)} · {formatKm(t.km)}</p>
    </div>
  )
}

function BlocoEnergia({ e, titulo }: { e: ConsumoEnergia; titulo?: string }) {
  const un = unidadeConsumo(e.unidade)
  const serie = e.serie.filter((t) => t.kmPorUnidade != null).map((t) => ({ data: formatDate(t.dataFim), valor: Number(t.kmPorUnidade!.toFixed(2)) }))
  return (
    <Card>
      <CardHeader title={titulo ?? 'Consumo por trecho'} icon="ri-speed-line" actions={<TendenciaBadge t={e.tendencia} variacao={e.variacaoPercentual} />} />
      <CardBody className="space-y-5">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi rotulo="Média ponderada" valor={e.mediaPonderada != null ? `${numeroBr(e.mediaPonderada, 1)} ${un}` : '—'} sub={`${formatKm(e.kmTotal)} em ${numeroBr(e.quantidadeTotal, 1)} ${UNIDADE_ENERGIA_LABEL[e.unidade]}`} />
          <Kpi rotulo="Mediana" valor={e.mediana != null ? `${numeroBr(e.mediana, 1)} ${un}` : '—'} sub={`${e.trechos} trecho(s) calculado(s)`} />
          <Kpi rotulo="Últimos 5" valor={e.mediaUltimos5 != null ? `${numeroBr(e.mediaUltimos5, 1)} ${un}` : '—'} />
          <Kpi rotulo="Custo por km" valor={e.custoPorKm != null ? formatMoney(e.custoPorKm) : '—'} />
        </div>

        {serie.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={serie} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#30363D" />
                <XAxis dataKey="data" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} width={40} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ background: '#161B22', border: '1px solid #30363D', borderRadius: 4, fontSize: 12 }}
                  formatter={(v) => [`${numeroBr(Number(v), 1)} ${un}`, 'Consumo']} />
                {e.mediaPonderada != null && <ReferenceLine y={e.mediaPonderada} stroke="#8B949E" strokeDasharray="4 4" />}
                <Line type="monotone" dataKey="valor" stroke="#FFB300" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="text-13 text-muted">Ainda não há trecho fechado para o gráfico. São precisos dois abastecimentos do mesmo tipo (cheio a cheio ou reserva a reserva).</p>}
        {serie.length > 0 && <p className="-mt-3 text-2xs text-muted">A linha tracejada é a média ponderada.</p>}
        {e.trechosSemCalculo > 0 && <p className="text-xs text-muted">{e.trechosSemCalculo} trecho(s) ficaram sem cálculo (âncoras de tipos diferentes). Não chutamos o consumo.</p>}

        <div className="grid gap-3 sm:grid-cols-2">
          <TrechoLinha rotulo="Melhor trecho" t={e.melhor} un={un} />
          <TrechoLinha rotulo="Pior trecho" t={e.pior} un={un} />
        </div>

        {e.ultimos5.length > 0 && (
          <div>
            <h5 className="mb-2 text-13 font-medium uppercase text-muted">Últimos 5 trechos</h5>
            <ul className="divide-y divide-border/70 text-13">
              {e.ultimos5.map((t) => (
                <li key={t.fimId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-muted">{formatDate(t.dataFim)} · {formatKm(t.km)}{t.tipo ? ` · ${TIPO_TRECHO_LABEL[t.tipo]}` : ''}{t.misto ? ' · misto' : t.combustivel ? ` · ${TIPO_COMBUSTIVEL_LABEL[t.combustivel]}` : ''}</span>
                  <span className="font-medium text-white">{t.kmPorUnidade != null ? `${numeroBr(t.kmPorUnidade, 1)} ${un}` : '—'}{t.custoPorKm != null ? ` · ${formatMoney(t.custoPorKm)}/km` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {e.cidadeEstrada && (
          <div>
            <h5 className="mb-2 text-13 font-medium uppercase text-muted">Cidade x estrada</h5>
            <div className="grid grid-cols-2 gap-3">
              <Kpi rotulo="Cidade" valor={`${numeroBr(e.cidadeEstrada.cidade.mediaPonderada, 1)} ${un}`} sub={`${e.cidadeEstrada.cidade.trechos} trechos`} />
              <Kpi rotulo="Estrada" valor={`${numeroBr(e.cidadeEstrada.estrada.mediaPonderada, 1)} ${un}`} sub={`${e.cidadeEstrada.estrada.trechos} trechos`} />
            </div>
          </div>
        )}

        {e.porCombustivel.length > 1 && (
          <div>
            <h5 className="mb-2 text-13 font-medium uppercase text-muted">Por combustível</h5>
            <ul className="divide-y divide-border/70 text-13">
              {e.porCombustivel.map((c, i) => (
                <li key={i} className="flex items-center justify-between py-2">
                  <span className="text-muted">{c.misto || c.combustivel == null ? 'Misto (combustíveis juntos)' : TIPO_COMBUSTIVEL_LABEL[c.combustivel]} · {c.trechos} trecho(s)</span>
                  <span className="font-medium text-white">{numeroBr(c.mediaPonderada, 1)} {un}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/** Aba Consumo: gráfico por trecho, estatísticas, ranking de postos e de combustíveis. */
export function ConsumoSecao({ veiculoId, de, ate }: { veiculoId: string; de: string; ate: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['veiculos', veiculoId, 'consumo', de, ate],
    queryFn: () => veiculosApi.consumo(veiculoId, { de: de || undefined, ate: ate || undefined }),
  })
  if (isLoading) return <Card><LoadingState /></Card>
  if (isError || !data) return <Card><ErrorState message={(error as Error)?.message || 'Não foi possível calcular o consumo.'} /></Card>
  if (data.abastecimentos === 0) return <Card><EmptyState icon="ri-speed-line" message="Sem abastecimentos no período para calcular o consumo." /></Card>

  const plug = data.energias.length > 1
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Kpi rotulo="Abastecimentos" valor={String(data.abastecimentos)} />
        <Kpi rotulo="Gasto total" valor={formatMoney(data.gastoTotal)} />
        <Kpi rotulo={plug ? 'Custo por km (conjunto)' : 'Custo por km'} valor={data.custoPorKm != null ? formatMoney(data.custoPorKm) : '—'} />
      </div>

      {data.energias.map((e) => (
        <BlocoEnergia key={e.unidade} e={e} titulo={plug ? (e.unidade === UnidadeEnergia.Kwh ? 'Consumo na eletricidade' : 'Consumo no combustível') : undefined} />
      ))}
      {plug && <p className="text-xs text-muted">Híbrido plug-in: o consumo é calculado separado por energia, e o custo por km considera as duas.</p>}

      <div className="grid items-start gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Ranking de postos" icon="ri-store-2-line" />
          <CardBody>
            {data.postos.length === 0 ? <p className="text-13 text-muted">Nenhum posto informado.</p> : (
              <ul className="divide-y divide-border/70 text-13">
                {data.postos.map((p, i) => (
                  <li key={p.cnpj ?? p.nome} className="flex items-center justify-between gap-3 py-2">
                    <span className="min-w-0 truncate text-white">{i + 1}. {p.nome}</span>
                    <span className="flex-shrink-0 text-right text-muted">{p.vezes}x · {formatMoney(p.gasto)}{p.precoMedioLitro != null ? ` · ${formatMoney(p.precoMedioLitro)}/L` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Combustíveis" icon="ri-gas-station-line" />
          <CardBody>
            <ul className="divide-y divide-border/70 text-13">
              {data.combustiveis.map((c) => (
                <li key={c.tipoCombustivel} className="flex items-center justify-between gap-3 py-2">
                  <span className="text-white">{TIPO_COMBUSTIVEL_LABEL[c.tipoCombustivel]}</span>
                  <span className="text-right text-muted">{c.vezes}x · {numeroBr(c.quantidade, 1)} {UNIDADE_ENERGIA_LABEL[c.unidade]} · {formatMoney(c.gasto)}{c.precoMedio != null ? ` · ${formatMoney(c.precoMedio)}/${UNIDADE_ENERGIA_LABEL[c.unidade]}` : ''}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}
